import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Tabs,
  Tab,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import {
  useGrantedAccess,
  useReceivedAccess,
  useGrantAccess,
  useRevokeAccess,
  AccessGrant,
} from '../../hooks/useTrustCircle'
import { useDevices } from '../../hooks/useDevices'
import { format } from 'date-fns'

const PERMISSION_OPTIONS = ['view_location', 'view_sensors', 'view_alerts', 'manage_device']

export default function TrustCirclePage() {
  const { t } = useTranslation()
  const { data: granted = [], isLoading: gLoading } = useGrantedAccess()
  const { data: received = [], isLoading: rLoading } = useReceivedAccess()
  const { data: devices = [] } = useDevices()
  const grantAccess = useGrantAccess()
  const revokeAccess = useRevokeAccess()

  const [tab, setTab] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [form, setForm] = useState({
    target_tenant_id: '',
    device_id: '',
    permissions: [] as string[],
    expires_at: '',
  })

  // Resolve device name from local device list (for devices owned by this tenant)
  const resolveDeviceName = (deviceId: string) => {
    const d = devices.find((dev) => dev.id === deviceId)
    return d?.name ?? ''
  }

  // Display name: prefer stored device_name, fallback to local lookup, last resort truncated ID
  const displayDeviceName = (ag: AccessGrant) => {
    if (ag.device_name) return ag.device_name
    const resolved = resolveDeviceName(ag.device_id)
    if (resolved) return resolved
    return ag.device_id ? `${ag.device_id.slice(0, 12)}…` : 'All'
  }

  const handleGrant = async () => {
    const deviceName = resolveDeviceName(form.device_id)
    await grantAccess.mutateAsync({
      ...form,
      device_name: deviceName,
      expires_at: form.expires_at || undefined,
    })
    analytics.track(EVENTS.TRUST_CIRCLE_DEVICE_SHARED, { device_id: form.device_id, permissions: form.permissions })
    setDialogOpen(false)
    setForm({ target_tenant_id: '', device_id: '', permissions: [], expires_at: '' })
  }

  const GrantCard = ({ ag }: { ag: AccessGrant }) => (
    <Card variant="outlined">
      <CardContent sx={{ py: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
            {ag.target_tenant_id.slice(0, 12)}…
          </Typography>
          <Tooltip title={t('trustCircle.revokeAccess')}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(ag.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Device: {displayDeviceName(ag)}
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
          {(ag.permissions ?? []).map((p) => (
            <Chip key={p} label={p} size="small" />
          ))}
        </Stack>
        {ag.expires_at && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Expires: {format(new Date(ag.expires_at), 'PP')}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box>
      <PageHeader
        title={t('trustCircle.title')}
        subtitle={t('trustCircle.subtitle')}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
            {t('trustCircle.grantAccess')}
          </Button>
        }
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`${t('trustCircle.accessGranted')} (${granted.length})`} />
        <Tab label={`${t('trustCircle.accessReceived')} (${received.length})`} />
      </Tabs>

      {tab === 0 && (
        <>
          {granted.length === 0 ? (
            <Alert severity="info">{t('trustCircle.noGrantedAccess')}</Alert>
          ) : (
            <Grid container spacing={2}>
              {granted.map((ag) => (
                <Grid item xs={12} sm={6} md={4} key={ag.id}>
                  <GrantCard ag={ag} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {received.length === 0 ? (
            <Alert severity="info">{t('trustCircle.noReceivedAccess')}</Alert>
          ) : (
            <Grid container spacing={2}>
              {received.map((ag) => (
                <Grid item xs={12} sm={6} md={4} key={ag.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: '12px !important' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                        From: {ag.owner_tenant_id.slice(0, 12)}…
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Device: {displayDeviceName(ag)}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                        {(ag.permissions ?? []).map((p) => (
                          <Chip key={p} label={p} size="small" color="primary" />
                        ))}
                      </Stack>
                      {ag.expires_at && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Expires: {format(new Date(ag.expires_at), 'PP')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Grant Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('trustCircle.grantAccess')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {grantAccess.isError && (
            <Alert severity="error">Failed to grant access</Alert>
          )}
          <TextField
            label="Target Tenant ID (UUID)"
            value={form.target_tenant_id}
            onChange={(e) => setForm((p) => ({ ...p, target_tenant_id: e.target.value }))}
            required fullWidth autoFocus
            helperText="UUID of the tenant to share with"
          />
          <TextField
            select label="Device"
            value={form.device_id}
            onChange={(e) => setForm((p) => ({ ...p, device_id: e.target.value }))}
            fullWidth
            helperText="Select a device to share"
          >
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select label="Permissions" value={form.permissions}
            onChange={(e) => {
              const val = e.target.value
              setForm((p) => ({ ...p, permissions: typeof val === 'string' ? val.split(',') : val as string[] }))
            }}
            SelectProps={{ multiple: true }}
            fullWidth
          >
            {PERMISSION_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Expires At (optional)"
            type="datetime-local"
            value={form.expires_at}
            onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleGrant}
            disabled={grantAccess.isPending || !form.target_tenant_id || !form.device_id || form.permissions.length === 0}
          >
            {t('trustCircle.grantAccess')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('trustCircle.revokeAccess')}
        message={t('trustCircle.revokeConfirm')}
        confirmLabel={t('trustCircle.revokeAccess')}
        confirmColor="error"
        onConfirm={() => {
          analytics.track(EVENTS.TRUST_CIRCLE_REVOKED, { grant_id: deleteTarget! })
          revokeAccess.mutate(deleteTarget!)
          setDeleteTarget(null)
        }}
        onClose={() => setDeleteTarget(null)}
        loading={revokeAccess.isPending}
      />
    </Box>
  )
}
